import { Environment, Lightformer } from '@react-three/drei'
import type { ProductConfig } from './types'

/**
 * Studio-omgeving: een softbox-opstelling, niet een setje puntlampen.
 *
 * Waarom dit zo belangrijk is: op een fotorealistisch model komt het grootste
 * deel van wat je ziet niet van lampen maar van wát er in het oppervlak
 * weerkaatst. Een `directionalLight` geeft één harde stip; een vlak van twee bij
 * één meter geeft de lange, zachte streep over een gespoten paneel die je op
 * elke productfoto ziet. Daarom zijn de lichtbronnen hier vlakken.
 *
 * De opstelling is de klassieke driepunts met een strip erbij:
 *
 * - **sleutellicht** groot en hoog van linksvoor, iets warm;
 * - **invullicht** aan de rechterkant, zwakker en koeler, zodat de schaduwzijde
 *   niet dichtloopt;
 * - **striplicht** smal en fel achter het product: dat is de lichtrand langs de
 *   contour die vorm van achtergrond scheidt;
 * - **bodemkaart** donker, want zonder dat wordt de onderkant van alles te licht
 *   en gaat het product zweven.
 *
 * Alles is genormaliseerd op `omgevingsSterkte` uit de config, zodat een donker
 * product opgeschroefd kan worden zonder de verhoudingen te verliezen.
 *
 * Levert de klant een eigen .hdr, dan wint die: `omgevingsBestand` in de config.
 */
export function Studio({ product }: { product: ProductConfig }) {
  const s = product.omgevingsSterkte

  if (product.omgevingsBestand) {
    return <Environment files={product.omgevingsBestand} environmentIntensity={s} />
  }

  return (
    <Environment resolution={512} frames={1}>
      {/* Sleutellicht: groot, hoog, linksvoor. De belangrijkste reflectie. */}
      <Lightformer
        form="rect" intensity={s * 5} color="#fff6ea"
        position={[-3.2, 5.2, 4.2]} rotation={[-0.62, -0.5, 0]} scale={[7, 5, 1]}
      />
      {/* Plafondkaart: vult de bovenzijde en geeft de lange streep over vlakken. */}
      <Lightformer
        form="rect" intensity={s * 3.2} color="#ffffff"
        position={[0, 7, 0]} rotation={[Math.PI / 2, 0, 0]} scale={[9, 9, 1]}
      />
      {/* Invullicht rechts: zwakker en koeler, houdt de schaduwzijde leesbaar. */}
      <Lightformer
        form="rect" intensity={s * 1.9} color="#d9e6ff"
        position={[5, 3, 1.5]} rotation={[0, -1.15, 0]} scale={[7, 5, 1]}
      />
      {/* Striplicht achter: de randlichtlijn langs de contour. */}
      <Lightformer
        form="rect" intensity={s * 6} color="#ffffff"
        position={[-1.2, 3.4, -5.5]} rotation={[0, Math.PI - 0.25, 0]} scale={[5, 0.9, 1]}
      />
      <Lightformer
        form="rect" intensity={s * 4} color="#eaf1ff"
        position={[3.4, 2.6, -4.6]} rotation={[0, Math.PI + 0.5, 0]} scale={[3.5, 0.7, 1]}
      />
      {/* Bodem: donker, anders zweeft het product. */}
      <Lightformer
        form="rect" intensity={s * 0.35} color="#3a3d45"
        position={[0, -3, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[14, 14, 1]}
      />
    </Environment>
  )
}
